import { MessagesButton } from './flow/MessagesDrawer';
import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { IntegrationCallOutcomeDto } from '@his/shared';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { visibleInterval } from '@/lib/polling';
import { dateOnly, money } from '@/lib/utils';
import { tr } from '@/lib/i18n-core';
import { DataTable, FilterField, SearchFilterBar } from '@/components/ui/layout';
import { Badge, Button, Select, StatusBadge } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

interface ReconciliationRow {
  id: string;
  reconciliationRef: string;
  claimRef: string;
  claimId: string | null;
  paymentDate: string | null;
  paymentMinor: number;
  nphiesFeeMinor: number;
  earlyPaymentFeeMinor: number;
  otherFeeMinor: number;
  netMinor: number;
  unallocatedMinor: number;
  status: string;
  note: string | null;
  paymentNoticeRef: string | null;
  paymentReconciliationId: string | null;
  noticeState: string;
  inboundNoticeRef: string | null;
  claim: { id: string; number: string; payer: { id: string; name: string } } | null;
}

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'destructive' | 'secondary'> = { posted: 'success', received: 'secondary', unmatched: 'warning', needs_review: 'destructive' };

export function ReconciliationTable({ claimId }: { claimId?: string }) {
  const { can, hasModule } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('');

  const list = useInfiniteQuery({
    queryKey: ['reconciliations', claimId ?? 'all', status],
    initialPageParam: '',
    queryFn: ({ pageParam }) =>
      get<{ items: ReconciliationRow[]; nextCursor: string | null }>(`/insurance/reconciliations${qs({ status: status || undefined, claimId, limit: 30, cursor: pageParam || undefined })}`),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: claimId ? false : visibleInterval(60_000),
  });
  const rows = list.data?.pages.flatMap((page) => page.items);

  const retry = useMutation({
    mutationFn: (id: string) => post(`/insurance/reconciliations/${id}/retry`, {}),
    onSuccess: () => {
      toast.success('Matched again');
      void queryClient.invalidateQueries({ queryKey: ['reconciliations'] });
      void queryClient.invalidateQueries({ queryKey: ['claims'] });
    },
    onError: (error) => toast.error('Could not match it', describeError(error)),
  });

  const notice = useMutation({
    mutationFn: (row: ReconciliationRow) =>
      post<IntegrationCallOutcomeDto>(row.paymentReconciliationId ? `/integrations/nphies/payment-reconciliations/${row.paymentReconciliationId}/notice` : `/integrations/nphies/reconciliations/${row.id}/notice`, {}),
    onSuccess: (outcome) => {
      if (outcome.status === 'succeeded') toast.success('The insurer was told', outcome.message);
      else toast.warning('Not sent', outcome.message);
      void queryClient.invalidateQueries({ queryKey: ['reconciliations'] });
    },
    onError: (error) => toast.error('Could not send the notice', describeError(error)),
  });

  return (
    <>
      {!claimId && (
        <>
          <p className="mb-2 text-xs text-muted-foreground">Payments the insurer reported, claim by claim. The fees NPHIES took are booked as an expense, the rest clears the panel receivable.</p>
          <SearchFilterBar
            filters={
              <FilterField label={tr('billing', 'status')}>
                <Select className="w-48" value={status} onChange={(event) => setStatus(event.target.value)}>
                  <option value="">Every payment</option>
                  <option value="posted">Applied</option>
                  <option value="unmatched">Matched no claim</option>
                  <option value="needs_review">Needs review</option>
                </Select>
              </FilterField>
            }
            onReset={status ? () => setStatus('') : undefined}
          />
        </>
      )}
      <DataTable
        dense
        rows={rows}
        loading={list.isLoading} refreshing={list.isFetching}
        partial={Boolean(list.hasNextPage)}
        error={list.error}
        rowKey={(row) => row.id}
        empty="No payments reported yet."
        footer={
          list.hasNextPage ? (
            <Button variant="outline" size="sm" className="mx-auto" onClick={() => void list.fetchNextPage()} loading={list.isFetchingNextPage}>
              Show more
            </Button>
          ) : undefined
        }
        columns={[
          { header: 'Date', sort: (row) => row.paymentDate, cell: (row) => (row.paymentDate ? dateOnly(row.paymentDate) : '—') },
          { header: 'Payment', sort: (row) => row.reconciliationRef, cell: (row) => <span className="font-mono text-xs">{row.reconciliationRef}</span> },
          { header: 'Claim', sort: (row) => row.claim?.number ?? row.claimRef, cell: (row) => row.claim?.number ?? <span className="text-muted-foreground">{row.claimRef}</span> },
          ...(claimId ? [] : [{ header: 'Payer', sort: (row: ReconciliationRow) => row.claim?.payer.name, cell: (row: ReconciliationRow) => row.claim?.payer.name ?? '—' }]),
          { header: 'Paid', align: 'right' as const, sort: (row) => row.paymentMinor, cell: (row) => money(row.paymentMinor) },
          { header: 'NPHIES fee', align: 'right' as const, sort: (row) => row.nphiesFeeMinor, cell: (row) => money(row.nphiesFeeMinor) },
          { header: 'Early fee', align: 'right' as const, sort: (row) => row.earlyPaymentFeeMinor, cell: (row) => money(row.earlyPaymentFeeMinor) },
          { header: 'Received', align: 'right' as const, sort: (row) => row.netMinor, cell: (row) => <span className="font-medium">{money(row.netMinor)}</span> },
          {
            header: 'Status',
            sort: (row) => row.status,
            cell: (row) => (
              <span title={row.note ?? undefined}>
                <StatusBadge status={row.status} tone={STATUS_VARIANT[row.status] ?? 'secondary'} />
                {row.unallocatedMinor > 0 && <span className="ms-1 text-xs text-destructive">{money(row.unallocatedMinor)} over</span>}
              </span>
            ),
          },
          {
            header: '',
            align: 'right' as const,
            cell: (row) => (
              <span className="flex justify-end gap-1">
                <MessagesButton entityType="reconciliation" entityId={row.id} title={tr('billing', 'nx_messages_for_reconciliation')} />
                {row.status === 'unmatched' && can('claim.manage') && (
                  <Button size="sm" variant="outline" loading={retry.isPending && retry.variables === row.id} onClick={() => retry.mutate(row.id)}>
                    Match again
                  </Button>
                )}
                {row.inboundNoticeRef && <Badge variant="secondary">Insurer sent its own notice</Badge>}
                {row.noticeState === 'queued' && <Badge variant="warning">Notice on its way</Badge>}
                {row.noticeState === 'sent' && <Badge variant="success">Insurer told</Badge>}
                {row.status === 'posted' && (row.noticeState === 'none' || row.noticeState === 'failed') && hasModule('nphies') && can('nphies.payment.notice') && (
                  <Button size="sm" variant="ghost" loading={notice.isPending && notice.variables?.id === row.id} onClick={() => notice.mutate(row)}>
                    {row.noticeState === 'failed' ? 'Tell the insurer again' : 'Tell the insurer the whole payment arrived'}
                  </Button>
                )}
              </span>
            ),
          },
        ]}
      />
    </>
  );
}

export function ReconciliationsTab() {
  return <ReconciliationTable />;
}
